import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Seller, User } from "types";
import { config } from "../config";
import { hasRegisteredSnippet, registerSnippet } from "./index";

const SNIPPET_NAME = "Google Tag Manager";

let isInjected = false;

const injectGtm = () => {
    if (isInjected) {
        return;
    }
    const id = config.google.tagManagerId;
    if (!id) {
        return;
    }

    // GTM Standard: Place script as high in <head> as possible
    // We inject dynamically before first script to achieve this

    const gtm_auth = config.google?.gtm_auth;
    const gtm_preview = config.google?.gtm_preview;
    const gtm_cookies_win = config.google.gtm_cookies_win;
    const gtmquery =
        gtm_auth && gtm_preview && gtm_cookies_win
            ? `&gtm_auth=${gtm_auth}&gtm_preview=${gtm_preview}&gtm_cookies_win=${gtm_cookies_win}`
            : "";

    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    const firstScript = document.querySelectorAll("script")[0];
    const gtmScript = document.createElement("script");
    gtmScript.async = true;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${id}${gtmquery}`;
    if (firstScript?.parentNode) {
        // Insert before first script (high in <head>)
        firstScript.parentNode.insertBefore(gtmScript, firstScript);
    } else {
        // Fallback: append to head
        document.head.append(gtmScript);
    }

    isInjected = true;
};

let userSellerDataPushed = false;

const pushUserSellerDataToGtm = (user: User, seller: Seller) => {
    if (userSellerDataPushed) {
        return;
    }

    const gtmEventName = config.google.gtmUserEventName || "Identify";

    window.dataLayer = window.dataLayer ?? [];

    const gtmData = {
        seller_id: seller.id.toString(),
        seller_name: seller.name,
        user_email: user.email,
        user_name: user.name,
        event: gtmEventName,
    };

    window.dataLayer.push(gtmData);

    if (window.gtag) {
        window.gtag("event", gtmEventName, {
            seller_id: seller.id.toString(),
            seller_name: seller.name,
            user_email: user.email,
            user_name: user.name,
        });
    }

    userSellerDataPushed = true;
};

registerSnippet(SNIPPET_NAME, !!config.google.tagManagerId, (user: User, seller: Seller) => {
    injectGtm();

    setTimeout(() => {
        pushUserSellerDataToGtm(user, seller);
    }, 1000);
});

export const useTrackGtmPageView = () => {
    const { pathname, search } = useLocation();
    const isLoaded = hasRegisteredSnippet(SNIPPET_NAME);
    useEffect(() => {
        if (!isLoaded) {
            return;
        }
        window.dataLayer = window.dataLayer ?? [];
        window.dataLayer.push({
            event: "page_view",
            page_title: document.title,
            page_location: window.location?.href,
            page_path: pathname + search,
        });
    }, [isLoaded, pathname, search]);
};

export const ensureGtmLoaded = () => injectGtm();
